import React, { useMemo } from 'react';
import { View, Text, useWindowDimensions } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { ChevronDown } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import type { SectionBucket } from '../../../src/challenges/types/challenges.types';
import { useCountdownToDate } from '../../../src/challenges/hooks/useCountdownToDate';
import { CHALLENGE_CARD_WIDE_INNER_MAX_WIDTH } from '../../../src/challenges/constants/challengeCardLayout.constants';
import {
  getChallengeCardHeroStyles,
  getHeroBgGradientYourMove,
  getHeroBgGradientLive,
  getHeroBgGradientDefault,
  getVsCircleGradient,
  getFloorGlowGradient,
  getHeroFooterChevronColor,
} from './ChallengeCardHero.styles';

interface ChallengeCardHeroProps {
  currentUserAvatarId?: string | null;
  currentUserAvatarConfig?: EquippedMap | null;
  opponentAvatarId?: string | null;
  opponentAvatarConfig?: EquippedMap | null;
  opponentName?: string;
  sectionBucket?: SectionBucket;
  stakeCoins?: number;
  hasBannerAbove?: boolean;
  firstGameDate?: string | null;
}

export const ChallengeCardHero: React.FC<ChallengeCardHeroProps> = ({
  currentUserAvatarId,
  currentUserAvatarConfig,
  opponentAvatarId,
  opponentAvatarConfig,
  opponentName,
  sectionBucket,
  stakeCoins,
  hasBannerAbove,
  firstGameDate,
}) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const useWideInnerAlign = windowWidth > CHALLENGE_CARD_WIDE_INNER_MAX_WIDTH;
  const styles = useMemo(() => getChallengeCardHeroStyles(theme), [theme]);
  const { display: countdownDisplay, isUrgent, segments } = useCountdownToDate(firstGameDate ?? null);

  const bgGradient = useMemo((): [string, string] => {
    if (sectionBucket === 'yourMove') return getHeroBgGradientYourMove(theme);
    if (sectionBucket === 'live') return getHeroBgGradientLive(theme);
    return getHeroBgGradientDefault(theme);
  }, [theme, sectionBucket]);

  const vsCircleGradient = useMemo(() => getVsCircleGradient(theme), [theme]);
  const floorGlowColors = useMemo(() => getFloorGlowGradient(theme), [theme]);
  const footerChevronColor = useMemo(() => getHeroFooterChevronColor(theme), [theme]);

  const oppDisplayName = (opponentName ?? 'Opp').trim() || 'Opp';

  const heroBody = (
    <>
      <View style={styles.container}>
        {/* You — left side facing right */}
        <View style={styles.playerSide}>
          <View style={styles.youLabel}>
            <Text style={styles.youLabelText}>YOU</Text>
          </View>
          <FriendAvatar
            avatarId={(currentUserAvatarId ?? null) as DisplayAvatarId | null}
            avatarConfig={currentUserAvatarConfig}
            size={88}
            variant="home"
            small
          />
        </View>

        {/* VS center */}
        <View style={styles.center}>
          <LinearGradient
            colors={vsCircleGradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.vsCircle}
          >
            <Text style={styles.vsText}>VS</Text>
          </LinearGradient>
          {stakeCoins != null && (
            <View style={styles.stakeRow}>
              <Image source={IDLE_COIN_SOURCE} style={styles.coinIcon} alt={WEB_IMAGE_ALT_DECORATIVE} />
              <Text style={styles.stakeText}>{stakeCoins}</Text>
            </View>
          )}
        </View>

        {/* Opponent — right side facing left */}
        <View style={[styles.playerSide, styles.playerSideRight]}>
          <View style={styles.oppLabel}>
            <Text
              style={styles.oppLabelText}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {oppDisplayName.toUpperCase()}
            </Text>
          </View>
          <FriendAvatar
            avatarId={(opponentAvatarId ?? null) as DisplayAvatarId | null}
            avatarConfig={opponentAvatarConfig}
            size={88}
            variant="away"
            nameForInitials={opponentName}
            small
          />
        </View>
      </View>

      {/* Footer: timer + chevron, centered below the 3-col layout */}
      <View style={styles.heroFooter}>
        {firstGameDate != null && countdownDisplay !== '' && (
          segments != null ? (
            <View style={[styles.timerInline, isUrgent && styles.timerInlineUrgent]}>
              <View style={styles.timerSegment}>
                <Text style={[styles.timerNumber, isUrgent && styles.timerNumberUrgent]}>{segments.days}</Text>
                <Text style={styles.timerLabel}>days</Text>
              </View>
              <Text style={[styles.timerColon, isUrgent && styles.timerNumberUrgent]}>:</Text>
              <View style={styles.timerSegment}>
                <Text style={[styles.timerNumber, isUrgent && styles.timerNumberUrgent]}>{segments.hours}</Text>
                <Text style={styles.timerLabel}>hrs</Text>
              </View>
              <Text style={[styles.timerColon, isUrgent && styles.timerNumberUrgent]}>:</Text>
              <View style={styles.timerSegment}>
                <Text style={[styles.timerNumber, isUrgent && styles.timerNumberUrgent]}>{segments.minutes}</Text>
                <Text style={styles.timerLabel}>min</Text>
              </View>
            </View>
          ) : (
            <View style={styles.timerInline}>
              <Text style={styles.timerStartedText}>Started</Text>
            </View>
          )
        )}
        <ChevronDown size={14} color={footerChevronColor} />
      </View>
    </>
  );

  return (
    <View style={hasBannerAbove ? styles.wrapperWithBannerAbove : styles.wrapper}>
      <LinearGradient
        colors={bgGradient}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={styles.gradientFill}
      />
      <LinearGradient
        colors={floorGlowColors}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={styles.floorGlow}
        pointerEvents="none"
      />

      {useWideInnerAlign ? <View style={styles.heroWideInner}>{heroBody}</View> : heroBody}
    </View>
  );
};
